import {CustomerSupportUspProps} from '@/molecules/USPs/CustomerSupport/typings';
import {CurrencySymbol} from '@/redux/reducers/typings';
import type {LinkHTMLAttributes, MetaHTMLAttributes} from 'react';

const metaTags = [
  {
    name: 'description',
    content:
      'The art marketplace. Buy wall art, original paintings, sculptures, limited edition prints and photography directly from independent artists.',
    /**
     * @description key is used to identify the metadata in the page head to avoid duplication
     * @link https://nextjs.org/docs/pages/api-reference/components/head#avoid-duplicated-tags
     */
    id: 'home-page-description',
  },
  {
    name: 'robots',
    content: 'index,follow',
    id: 'home-page-robots',
  },
  {
    property: 'og:site_name',
    content: 'Artfinder',
    id: 'home-page-og-site-name',
  },
  {
    property: 'og:title',
    content: 'Buy Original Art, Wall Art & Art Prints | Artfinder',
    id: 'home-page-og-title',
  },
  {
    property: 'og:description',
    content:
      'The art marketplace. Buy wall art, original paintings, sculptures, limited edition prints and photography directly from independent artists.',

    id: 'home-page-og-description',
  },
  {
    property: 'og:image',
    content:
      'https://d3rf6j5nx5r04a.cloudfront.net/xmewmbFMA1h9nOHCcoO_HcUCiEU=/1200x630/content/f/3/c628317dd64942fd8f0b6b49c5d8f5f5.jpg',

    id: 'home-page-og-image',
  },
  {
    name: 'twitter:image',
    content:
      'https://d3rf6j5nx5r04a.cloudfront.net/xmewmbFMA1h9nOHCcoO_HcUCiEU=/1200x630/content/f/3/c628317dd64942fd8f0b6b49c5d8f5f5.jpg',
    id: 'home-page-twitter-image',
  },
  {
    name: 'twitter:description',
    content:
      'The art marketplace. Buy wall art, original paintings, sculptures, limited edition prints and photography directly from independent artists.',
    id: 'home-page-twitter-description',
  },
  {
    name: 'twitter:card',
    content: 'summary_large_image',
    id: 'home-page-twitter-card',
  },
  {
    name: 'twitter:site',
    content: '@artfinder',
    id: 'home-page-twitter-site',
  },
];

/**
 * @description generate home page's metadata
 * @return {{metaTags: Record<string, unknown>[], links: Record<string, unknown>[], title: string, }} table of of metadata attributes
 */
export const metaData = (): {
  title: string;
  metaTags: (MetaHTMLAttributes<HTMLMetaElement> & {id: string})[];
  links: (LinkHTMLAttributes<HTMLLinkElement> & {id: string})[];
  regionDescription: Record<
    | 'uspBottom'
    | 'joinMailingList'
    | 'shopByCategory'
    | 'shopByPrice'
    | 'popularArtists'
    | 'discoverArtists'
    | 'editorsPicks'
    | 'customerReviews',
    string
  >;
} => ({
  title: 'Buy Original Art, Wall Art & Art Prints | Artfinder',
  metaTags,
  links: [
    {
      id: 'home-page-favicon-link',
      rel: 'icon',
      href: '/favicon.ico',
    },
    {
      id: 'home-page-canonical-link',
      rel: 'canonical',
      href: 'https://www.artfinder.com/',
    },
  ],
  regionDescription: {
    uspBottom:
      'This page section is the customer support area, consisting of three cards with information on customer care, commissioning an artist, and free returns. Each card has a link to more information.',
    shopByCategory:
      'This page section is the shop by category area, consisting of a header with a title and a view all link, and a list of categories with links to each category page.',
    joinMailingList:
      'Sign up below to join Artfinder’s email newsletter, by filling in the field below with your email address then press enter key or click the subscribe button.',
    shopByPrice:
      'This is the shop by price section, with links to shop art by price range. How to use this section: click on the button link to view art within the price range.',
    popularArtists:
      'This is the popular artists content, represented by list of 4 artists with links to individual artist pages. How to use this section: in the list click the artist card to view the individual artist page.',
    discoverArtists:
      'This is the discover artists content, a variety of artists around the world: Painters. Sculptors. Photographers. And then some. Represented by list of 8 artists with links to individual artist pages.',

    editorsPicks:
      'This section contains collections, made by our curation team, of carefully selected artworks around various topics and themes. Represented by list of 4 best recommended seasonal picks with links to the selection page.',

    customerReviews:
      'This section contains customer reviews by TrustPilot, represented by the Artfinder rating and a list of reviews, each is linked to the TrustPilot review page.',
  },
});

export const customerSupportUspContent: CustomerSupportUspContentProps = {
  title: 'Our unique selling points',
  cards: [
    {
      icon: 'customer-support',
      title: 'Exceptional customer care',
      subTitle: 'Our dedicated support team is always on hand to help.',
      linkHref: '/customer-care/',
      linkText: 'More info',
    },
    {
      icon: 'image',
      title: 'Commission an artist',
      subTitle:
        "Can't quite find the perfect piece? Or looking for a family or pet portrait? Commission an artist and make your dream a reality.",
      linkHref: '/commissions/',
      linkText: 'More info',
    },
    {
      icon: 'truck',
      title: 'Free returns',
      subTitle:
        "If you're not completely in love with your artwork, you can return it for free. No questions asked.",
      linkHref: '/free-returns/',
      linkText: 'More info',
    },
  ],
};

export const getShopByCategoryContent = ({isNewNavigation}: {isNewNavigation: boolean}) => ({
  title: 'Shop by category',
  links: {
    viewAllLinkHref: '/art/',
    categories: [
      {
        id: '1',
        linkText: 'Paintings',
        linkHref: '/art/product_category-painting/',
        slug: 'product_category-painting',
      },
      {
        id: '2',
        linkText: isNewNavigation ? 'Prints' : 'Printmaking',
        linkHref: isNewNavigation
          ? '/art/product_category-prints/'
          : '/art/product_category-printmaking/',
        slug: isNewNavigation ? 'product_category-prints' : 'product_category-printmaking',
      },
      {
        id: '3',
        linkText: 'Photography',
        linkHref: '/art/product_category-photography/',
        slug: 'product_category-photography',
      },
      {
        id: '4',
        linkText: 'Sculptures',
        linkHref: '/art/product_category-sculpture/',
        slug: 'product_category-sculpture',
      },
      {
        id: '5',
        linkText: 'Abstract art',
        linkHref: '/art/subject-abstract-conceptual/',
        slug: 'subject-abstract-conceptual',
      },
      {
        id: '6',
        linkText: 'Landscapes and sea sky',
        linkHref: '/art/subject-landscapes-sea-sky/',
        slug: 'subject-landscapes-sea-sky',
      },
      {
        id: '7',
        linkText: 'Portraits',
        linkHref: '/art/subject-people-portraits/',
        slug: 'subject-people-portraits',
      },
      {
        id: '8',
        linkText: 'Impressionistic art',
        linkHref: '/art/style-impressionistic/',
        slug: 'style-impressionistic',
      },
      {
        id: '9',
        linkText: 'Nudes',
        linkHref: '/art/subject-nudes-erotic/',
        slug: 'subject-nudes-erotic',
      },
      {
        id: '10',
        linkText: 'Realistic',
        linkHref: '/art/style-photorealistic/product_category-painting/',
        slug: 'style-photorealistic',
      },
      {
        id: '11',
        linkText: 'Still life',
        linkHref: '/art/subject-still-life/',
        slug: 'subject-still-life',
      },
      {
        id: '12',
        linkText: 'Ready to hang art',
        linkHref: '/art/framed_or_ready_to_hang-true/',
        slug: 'framed_or_ready_to_hang-true',
      },
      {
        id: '13',
        linkText: 'Animals and birds',
        linkHref: '/art/subject-animals-birds/',
        slug: 'subject-animals-birds',
      },
      {
        id: '14',
        linkText: 'Drawings',
        linkHref: '/art/product_category-drawing/',
        slug: 'product_category-drawing',
      },
    ],
  },
});

export const shopByPriceContent = (currency: CurrencySymbol) => ({
  title: 'Shop by price',
  links: [
    {
      id: '1',
      linkText: `${currency}500 and under`,
      linkHref: '/art/price_max-500/',
      slug: 'price_max-500',
    },
    {
      id: '2',
      linkText: `${currency}1000 and under`,
      linkHref: '/art/price_max-1000/',
      slug: 'price_max-1000',
    },
    {
      id: '3',
      linkText: `${currency}1000 and over`,
      linkHref: '/art/price_min-1000/',
      slug: 'price_min-1000',
    },
    {
      id: '4',
      linkText: `${currency}3000 and over`,
      linkHref: '/art/price_min-3000/',
      slug: 'price_min-3000',
    },
  ],
});

export const mostPopularSearches = (currency: CurrencySymbol) => ({
  title: 'Most popular searches',
  list: [
    {
      id: 1,
      label: 'Paintings',
      link: '/art/product_category-painting/',
      slug: 'product_category-painting',
    },
    {
      id: 2,
      label: '20% sale',
      link: '/art/in_sale-20/',
      slug: 'in_sale-20',
    },
    {
      id: 3,
      label: `${currency}500 and under`,
      link: '/art/price_max-500/',
      slug: 'price_max-500',
    },
    {
      id: 4,
      label: 'Sculptures',
      link: '/art/product_category-sculpture/',
      slug: 'product_category-sculpture',
    },
    {
      id: 5,
      label: 'Abstract art',
      link: '/art/subject-abstract-conceptual/',
      slug: 'subject-abstract-conceptual',
    },
    {
      id: 6,
      label: 'Landscapes',
      link: '/art/subject-landscapes-sea-sky/',
      slug: 'subject-landscapes-sea-sky',
    },
    {
      id: 7,
      label: `${currency}1000 and over`,
      link: '/art/price_min-1000/',
      slug: 'price_min-1000',
    },
  ],
});

export const discoverArtistsContent = {
  title: 'Discover artists from around the world',
  subTitle:
    'Painters. Sculptors. Photographers. And then some. Discover the artists that make our world go round.',
};

export const editorsPicksContent = {
  title: 'Editors’ picks',
  viewAllLinkHref: '/editors-picks/',
};

export const brandContent = {
  title: 'Where Artfinder got featured',
  images: [
    {
      id: 'bbc',
      src: 'https://d2m7ibezl7l5lt.cloudfront.net/img/logos/360w/bbc.png',
      alt: 'BBC logo',
      width: 360,
      height: 104,
    },
    {
      id: 'fastCompany',
      src: 'https://d2m7ibezl7l5lt.cloudfront.net/img/logos/360w/fast_company.png',
      alt: 'FastCompany logo',
      width: 360,
      height: 52,
    },
    {
      id: 'bloomberg',
      src: 'https://d2m7ibezl7l5lt.cloudfront.net/img/logos/360w/bloomberg.png',
      alt: 'Bloomberg logo',
      width: 360,
      height: 67,
    },
    {
      id: 'sundayTimes',
      src: 'https://d2m7ibezl7l5lt.cloudfront.net/img/logos/360w/sunday_times.png',
      alt: 'The Sunday Times logo',
      width: 360,
      height: 80,
    },
    {
      id: 'cnn',
      src: 'https://d2m7ibezl7l5lt.cloudfront.net/img/logos/360w/cnn.png',
      alt: 'CNN logo',
      width: 360,
      height: 168,
    },
  ],
};

export const customerReviewsContent = {
  title: 'What our customers think',
};

interface CustomerSupportUspContentProps {
  title: string;
  cards: CustomerSupportUspProps[];
}
